<template>
  <a-modal
    class="forget_password_modal"
    v-model:open="visible"
    title="Forgot password"
    @cancel="handleCancel"
    :footer="null"
  >
    <div class="tip">
      <span>
        If this email address is already associated with a JoyJobs account, we will send a verification code to this
        email address.
      </span>
    </div>
    <template v-if="step === 1">
      <a-form ref="formRef1" :model="formState" class="modal_form">
        <a-form-item
          validateTrigger="blur"
          label=""
          name="email"
          :rules="[
            { required: true, message: 'Enter your email address' },
            { type: 'email', message: 'Email is not compliant' },
          ]"
        >
          <a-input v-model:value="formState.email" placeholder="Enter your email address" autocomplete="off" />
        </a-form-item>
        <a-form-item
          validateTrigger="blur"
          label=""
          name="verifyCode"
          :rules="[{ required: true, message: 'Enter code' }]"
        >
          <a-input v-model:value="formState.verifyCode" placeholder="Enter code" />
          <button
            class="send_code_btn"
            @click.prevent="sendSMSCode"
            :style="{ color: sendSMSCodeText !== 'Send code' ? 'rgba(61, 61, 61, 0.5)' : '' }"
          >
            {{ sendSMSCodeText }}
          </button>
        </a-form-item>
        <a-button type="primary" class="next_btn" @click.prevent="handleStep1Submit" :disabled="isSubmitDisabled">
          Continue
          <ArrowRightOutlined class="btn_arrow" />
        </a-button>
      </a-form>
    </template>
    <template v-else>
      <a-form ref="formRef2" :model="formState" class="modal_form">
        <a-form-item validateTrigger="change" label="" name="newPassword" :rules="[{ validator: validatePass1 }]">
          <a-input-password
            v-model:value="formState.newPassword"
            placeholder="Enter password"
            autocomplete="off"
            :visibilityToggle="true"
          />
          <div v-if="isShowValidPwd" class="valid_pwd">
            <img
              v-if="isValidPwdCharacter"
              class="pwd_icon"
              src="@/assets/images/pwd_success.svg"
              alt="Validation Icon"
            />
            <img
              v-if="!isValidPwdCharacter"
              class="pwd_icon"
              src="@/assets/images/pwd_error.svg"
              alt="Validation Icon"
            />
            <span :class="{ valid_success: isValidPwdCharacter }">The password must contain letters and numbers</span>
          </div>
          <div v-if="isShowValidPwd" class="valid_pwd">
            <img v-if="isValidPwdLength" class="pwd_icon" src="@/assets/images/pwd_success.svg" alt="Validation Icon" />
            <img v-if="!isValidPwdLength" class="pwd_icon" src="@/assets/images/pwd_error.svg" alt="Validation Icon" />
            <span :class="{ valid_success: isValidPwdLength }">The password must be at least 8 characters long</span>
          </div>
        </a-form-item>
        <a-form-item validateTrigger="blur" label="" name="confirmPassword" :rules="[{ validator: validatePass2 }]">
          <a-input-password
            v-model:value="formState.confirmPassword"
            placeholder="Enter again"
            autocomplete="off"
            :visibilityToggle="true"
          />
        </a-form-item>
        <a-button type="primary" class="submit_btn" @click.prevent="handleStep2Submit" :disabled="isSubmitDisabled">
          Done and log in
          <ArrowRightOutlined class="btn_arrow" />
        </a-button>
      </a-form>
    </template>
  </a-modal>
</template>

<script setup>
import { ArrowRightOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { ref, defineExpose, reactive, toRaw, defineEmits } from 'vue';

import { forgetPassVerifyCode, forgetPassSubmit, forgetSendCode } from '@/api/account/forget.js';

const emit = defineEmits(['open-login']);
const visible = ref(false);
const step = ref(1);
const formRef1 = ref(null);
const formRef2 = ref(null);
const formState = reactive({
  email: '',
  verifyCode: '',
  newPassword: '',
  confirmPassword: '',
});
const isSubmitDisabled = ref(false);
const isSMSCodeDisabled = ref(false);
const intervalId = ref(null);
const sendSMSCodeText = ref('Send code');
const isShowValidPwd = ref(false);
const isValidPwdCharacter = ref(false);
const isValidPwdLength = ref(false);

const handleCancel = () => {
  visible.value = false;
  step.value = 1; // Reset to step 1
};

const openLoginModal = () => {
  visible.value = false;
  emit('open-login');
};

const validatePass1 = async (_rule, value) => {
  isShowValidPwd.value = true;
  if (!/(?=.*[a-zA-Z])(?=.*[0-9])/.test(value)) {
    isValidPwdCharacter.value = false;
  } else {
    isValidPwdCharacter.value = true;
  }
  if (value.length < 8) {
    isValidPwdLength.value = false;
  } else {
    isValidPwdLength.value = true;
  }
  if (!isValidPwdCharacter.value || !isValidPwdLength.value) {
    return Promise.reject();
  } else {
    return Promise.resolve();
  }
};

const validatePass2 = async (_rule, value) => {
  if (value === '') {
    return Promise.reject('Enter a new password');
  } else if (value !== formState.newPassword) {
    return Promise.reject('The two passwords do not match');
  } else {
    return Promise.resolve();
  }
};
const startCountdown = (time) => {
  if (isSMSCodeDisabled.value) return;
  if (!time) return;
  isSMSCodeDisabled.value = true;
  var timeLeft = time;
  intervalId.value = setInterval(() => {
    if (timeLeft > 0) {
      timeLeft--;
      sendSMSCodeText.value = timeLeft + ' resend';
    } else {
      clearInterval(intervalId.value);
      timeLeft = 60;
      isSMSCodeDisabled.value = false;
      sendSMSCodeText.value = 'Send code';
    }
  }, 1000);
};

const sendSMSCode = () => {
  formRef1.value.validateFields(['email']).then(() => {
    isSMSCodeDisabled.value = true;
    forgetSendCode({ email: formState.email }).then((res) => {
      isSMSCodeDisabled.value = false;
      startCountdown(60);
      if (res.code == 0) {
        message.success(res.msg);
      } else {
        message.error(res.msg);
      }
    });
  });
};

const handleStep1Submit = () => {
  formRef1.value.validate().then(() => {
    isSubmitDisabled.value = true;
    forgetPassVerifyCode({ email: formState.email, verifyCode: formState.verifyCode }).then((res) => {
      isSubmitDisabled.value = false;
      if (res.code == 0) {
        step.value = 2;
      } else {
        message.error(res.msg);
      }
    });
  });
};

const handleStep2Submit = () => {
  formRef2.value.validate().then(() => {
    isSubmitDisabled.value = true;
    forgetPassSubmit(toRaw(formState)).then((res) => {
      isSubmitDisabled.value = false;
      if (res.code == 0) {
        message.success(res.msg);
        openLoginModal();
      } else {
        message.error(res.msg);
      }
    });
  });
};

const open = () => {
  visible.value = true;
};

defineExpose({
  open,
});
</script>

<style lang="less">
.forget_password_modal {
  color: #05644f;
  font-size: 16px;
  .ant-modal-content {
    border-radius: 20px;
    padding: 32px;
  }
  .ant-modal-title {
    color: #05644f;
    font-size: 20px;
    font-weight: bold;
    line-height: normal;
  }
  .ant-modal-close-icon {
    color: #05644f;
  }
  .tip {
    margin-bottom: 25px;
  }
  .modal_form {
    .ant-input,
    .ant-input-password {
      border: 1px solid rgba(5, 100, 79, 0.5);
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
      border-radius: 16px;
      padding: 10px 24px;
      font-size: 14px;
      color: #05644f;
    }
    .ant-input-password {
      padding: 0 10px 0 0;
      .ant-input {
        border: none;
      }
    }
    .ant-input::placeholder {
      color: rgba(5, 100, 79, 0.33);
    }
    .ant-input-password-icon,
    .ant-input-password-icon:hover {
      color: #05644f;
      font-size: 18px;
    }
    .ant-form-item-explain-error {
      margin-bottom: 24px;
    }
    .valid_pwd {
      margin: 10px 0;
      font-size: 12px;
      color: #f04646;
      .pwd_icon {
        width: 14px;
        height: 14px;
        margin-right: 6px;
        vertical-align: text-bottom;
      }
      .valid_success {
        color: #05644f;
      }
    }
  }
  .next_btn,
  .submit_btn {
    background: #05644f;
    border-radius: 100px;
    color: #fff;
    font-size: 18px;
    font-weight: bold;
    line-height: normal;
    display: block;
    width: 100%;
    padding: 10px 20px;
    height: auto;
    margin-top: 20px;
    &:hover {
      background: #05644f;
      color: #fff;
    }
    .btn_arrow {
      margin-left: 10px;
      font-size: 18px;
    }
  }
  .send_code_btn {
    position: absolute;
    right: 24px;
    top: 12px;
    color: #05644f;
    background: transparent;
    border: none;
    cursor: pointer;
    font-weight: 500;
  }
}
</style>
